/**
 * Setup master data the office prepares before the pilot runs:
 *   /orgs        GET ?kind (plain array) · GET /:id · POST { name, kind, code, accreditationNo?, accreditationValidTo? }
 *                PATCH /:id { name?, accreditationNo?, accreditationValidTo? }       orgs:read | create | update
 *                (uc-core src/modules/org/org.controller.ts; no delete)
 *   /lab-limits  GET ?cropId&panel&requiredForRelease&search&page&limit · POST CreateLabLimitDto (next version)
 *                DELETE /:id (retire)                                                 lab-limits:read | create | delete
 *                (uc-core src/modules/lab/controllers/lab-reports.controller.ts LabLimitsController)
 *   /skus        GET ?cropId&search&page&limit · POST CreateSkuDto · PATCH /:id UpdateSkuDto · DELETE /:id
 *                                                                                    skus:read | create | update | delete
 *                (uc-core src/modules/pack/controllers/pack.controllers.ts SkusController; 409 SKU_IN_USE)
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { getList, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { bool, iso, num, numOrNull, str, strOrNull } from './mappers'

/** Today on this computer's calendar (YYYY-MM-DD). */
export function localToday(now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`
}

// ============================== organisations ==============================

export interface OrgRow {
  id: string
  name: string
  kind: string
  code: string | null
  accreditationNo: string | null
  accreditationValidTo: string | null
  createdAt: string | null
}
/** The org entity serialises camelCase; snake_case is read too in case a raw query sends it. */
export const mapOrg = (r: Record<string, unknown>): OrgRow => ({
  id: str(r.id),
  name: str(r.name),
  kind: str(r.kind),
  code: strOrNull(r.code),
  accreditationNo: strOrNull(r.accreditationNo ?? r.accreditation_no),
  accreditationValidTo: strOrNull(r.accreditationValidTo ?? r.accreditation_valid_to)?.slice(0, 10) ?? null,
  createdAt: iso(r.createdAt ?? r.created_at),
})

/** Same cache family as reference useOrgs (the lab / buyer pickers), so a save refreshes them too. */
const ORGS_KEY = [...ck.reference, 'orgs'] as const

export function useOrgList(kind?: string) {
  return useQuery({
    queryKey: [...ORGS_KEY, 'setup', kind ?? 'all'],
    queryFn: () => getList('/orgs', { kind }, mapOrg),
    staleTime: 60_000,
  })
}

export interface OrgBody {
  kind?: string
  code?: string
  name?: string
  accreditationNo?: string
  accreditationValidTo?: string
}
export function useSaveOrg({ inline = false } = {}) {
  return useConsoleMutation<{ id?: string; body: OrgBody }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/orgs/${id}`, body) : api.post('/orgs', body)),
    invalidate: [ORGS_KEY],
    success: (_d, v) => (v.id ? 'Saved' : v.body.kind === 'BUYER' ? 'Buyer added' : 'Lab added'),
    errorTitle: 'Not saved',
    onError: () => inline,
  })
}

// ============================== lab limits ==============================

export interface LabLimitRow {
  id: string
  cropId: string | null
  cropName: string | null
  panel: string
  analyte: string | null
  rule: string
  limitValue: string | null
  maxLoq: string | null
  uom: string
  requiredForRelease: boolean
  source: string
  version: number
  effectiveFrom: string
  createdAt: string | null
}
export const mapLabLimit = (r: Record<string, unknown>): LabLimitRow => ({
  id: str(r.id),
  cropId: strOrNull(r.crop_id),
  cropName: strOrNull(r.crop_name),
  panel: str(r.panel),
  analyte: strOrNull(r.analyte),
  rule: str(r.rule),
  limitValue: strOrNull(r.limit_value),
  maxLoq: strOrNull(r.max_loq),
  uom: str(r.uom),
  requiredForRelease: bool(r.required_for_release),
  source: str(r.source),
  version: num(r.version),
  effectiveFrom: str(r.effective_from).slice(0, 10),
  createdAt: iso(r.created_at),
})

const LIMITS_KEY = [...ck.quality, 'lab-limits'] as const

export function useLabLimits(
  f: PageParams & { cropId?: string; panel?: string; requiredForRelease?: boolean; search?: string },
) {
  return usePageQuery([...LIMITS_KEY, f], () => getPage('/lab-limits', { page: 1, limit: 500, ...f }, mapLabLimit))
}

export function useCreateLabLimit({ inline = false } = {}) {
  return useConsoleMutation<Record<string, unknown>>({
    mutationFn: (body) => api.post('/lab-limits', body),
    invalidate: [LIMITS_KEY],
    success: 'Limit saved',
    errorTitle: 'Limit not saved',
    onError: () => inline,
  })
}

export function useRetireLabLimit() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/lab-limits/${id}`),
    invalidate: [LIMITS_KEY],
    success: 'Limit retired',
    errorTitle: 'Not retired',
  })
}

// ============================== products (SKUs) ==============================

export interface SkuRow {
  id: string
  code: string
  name: string
  cropId: string
  cropName: string | null
  netG: number
  gtin: string | null
  shelfLifeDays: number | null
  toleranceG: string | null
  pouchesPerCarton: number | null
  updatedAt: string | null
}
export const mapSkuRow = (r: Record<string, unknown>): SkuRow => ({
  id: str(r.id),
  code: str(r.code),
  name: str(r.name),
  cropId: str(r.crop_id),
  cropName: strOrNull(r.crop_name),
  netG: num(r.net_g),
  gtin: strOrNull(r.gtin),
  shelfLifeDays: numOrNull(r.shelf_life_days),
  toleranceG: strOrNull(r.tolerance_g),
  pouchesPerCarton: numOrNull(r.pouches_per_carton),
  updatedAt: iso(r.updated_at),
})

/** Under ck.pack 'skus' so the packing boards' product pickers refresh after a save. */
const SKUS_KEY = [...ck.pack, 'skus'] as const

export function useSkuList(f: PageParams & { cropId?: string; search?: string }) {
  return usePageQuery([...SKUS_KEY, 'setup', f], () => getPage('/skus', { page: 1, limit: 100, ...f }, mapSkuRow))
}

export function useSaveSku({ inline = false } = {}) {
  return useConsoleMutation<{ id?: string; body: Record<string, unknown> }>({
    mutationFn: ({ id, body }) => (id ? api.patch(`/skus/${id}`, body) : api.post('/skus', body)),
    invalidate: [SKUS_KEY],
    success: (_d, v) => (v.id ? 'Product saved' : 'Product added'),
    errorTitle: 'Product not saved',
    onError: () => inline,
  })
}

export function useDeleteSku() {
  return useConsoleMutation<string>({
    mutationFn: (id) => api.delete(`/skus/${id}`),
    invalidate: [SKUS_KEY],
    success: 'Product removed',
    errorTitle: 'Not removed',
  })
}
